import { appSyncConfig } from './config.mjs';
import { queries } from './queries.mjs';
import { logMessage, showSpinner, hideSpinner, getWebId } from './utils.mjs';
import { locale as i18nLocale } from 'virtual:i18n';

const APP_VERSION = '1.0.0'; 
const CLIENT_INFO = `web/${i18nLocale.split('-')[0]}`;

/**
 * Generic helper for AppSync API calls
 */
async function callApi(query, variables = {}, options = {}) {
    if (!options.silent) showSpinner();
    try {
        const response = await fetch(appSyncConfig.url, {
            method: 'POST',
            headers: {
                'Accept': 'application/json, text/javascript',
                'Content-Type': 'application/json; charset=utf-8',
                'Content-Encoding': 'amz-1.0',
                'X-Api-Key': appSyncConfig.apiKey,
                'Host': appSyncConfig.host,
                'splitpal-app-version': CLIENT_INFO,
                'splitpal-app-platform': 'web',
                ...(options.headers || {})
            },
            body: JSON.stringify({ query, variables })
        });

        const data = await response.json();
        if (data.errors) throw new Error(data.errors[0].message);
        return data.data;
    } finally {
        if (!options.silent) hideSpinner();
    }
}

const LS_GROUPS_KEY = 'splitpal:groups';

function loadStoredGroups() {
    try {
        const raw = localStorage.getItem(LS_GROUPS_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        console.warn('Failed to load groups from localStorage', e);
        return [];
    }
}

function saveGroupToStorage(group) {
    try {
        if (!group || !group.id) return;
        const list = loadStoredGroups();
        const idx = list.findIndex(g => g && g.id === group.id);

        // Only update if incoming group is newer (or doesn't exist yet)
        if (idx >= 0) {
            const existingGroup = list[idx];
            // Compare mv (model version) - only update if incoming is newer
            if (group.mv !== undefined && existingGroup.mv !== undefined) {
                if (group.mv <= existingGroup.mv) {
                    console.log('Skipping localStorage update - existing group is newer or same', {
                        incoming: group.mv,
                        existing: existingGroup.mv
                    });
                    return;
                }
            }
            list[idx] = group;
        } else {
            list.push(group);
        }
        localStorage.setItem(LS_GROUPS_KEY, JSON.stringify(list));
    } catch (e) {
        console.warn('Failed to save group to localStorage', e);
    }
}

// Fetch group data using the getGroup query
export async function fetchGroup(groupId) {
    const data = await callApi(queries.getGroup, { groupId });
    if (!data || !data.getGroup) throw new Error('Group not found');

    const group = data.getGroup;
    saveGroupToStorage(group);
    return group;
}

// Edit an expense in a group
export async function editExpense(groupId, expenseId, input, participants) {
    const data = await callApi(queries.editExpense, {
        groupId,
        expenseId,
        input,
        participants
    });

    if (!data || !data.editExpense) throw new Error('Failed to edit expense');

    const updatedGroup = data.editExpense;
    saveGroupToStorage(updatedGroup);
    return updatedGroup;
}

// Add a new expense to a group
export async function addExpense(groupId, input, participants) {
    const data = await callApi(queries.addExpense, {
        groupId,
        input,
        participants
    });

    if (!data || !data.addExpense) throw new Error('Failed to add expense');

    const updatedGroup = data.addExpense;
    saveGroupToStorage(updatedGroup);
    return updatedGroup;
}

// Add a new participant to a group
export async function addParticipant(groupId, name) {
    const data = await callApi(queries.addParticipant, {
        groupId,
        name
    });

    if (!data || !data.addParticipant) throw new Error('Failed to add participant');

    const updatedGroup = data.addParticipant;
    saveGroupToStorage(updatedGroup);
    return updatedGroup;
}

// Get the signed URL for the receipt image
export async function getExpenseImageUrl(groupId, expenseId) {
    try {
        const data = await callApi(queries.getExpenseImageUrl, {
            groupId,
            expenseId
        }, { silent: true });

        return data?.getExpenseImageUrl || null;
    } catch (e) {
        console.error('Failed to get expense image url', e);
        return null;
    }
}
// Get public user data
export async function getPublicUserData(userId) {
    try {
        const data = await callApi(queries.getPublicUserData, { userId }, { silent: true });
        return data?.getPublicUserData || null;
    } catch (e) {
        console.error('Failed to fetch public user data', e);
        return null;
    }
}

// Rename a group
export async function renameGroup(groupId, name) {
    const data = await callApi(queries.renameGroup, {
        groupId,
        name
    });

    if (!data || !data.renameGroup) throw new Error('Failed to rename group');

    const updatedGroup = data.renameGroup;
    saveGroupToStorage(updatedGroup);
    return updatedGroup;
}

/**
 * Changes the base currency for a group
 * @param {string} groupId - Group ID
 * @param {string} currency - New currency code
 * @returns {Promise<Object>} The updated group object
 */
export async function changeBaseCurrency(groupId, currency) {
    const data = await callApi(queries.changeBaseCurrency, { groupId, currency });
    if (!data || !data.changeBaseCurrency) throw new Error('Failed to change base currency');

    const updatedGroup = data.changeBaseCurrency;
    saveGroupToStorage(updatedGroup);
    return updatedGroup;
}

/**
 * Links a single participant to an existing linked participant group
 * @param {string} groupId - Group ID
 * @param {number} participant - Index of the participant to link
 * @param {number} linkedParticipantsIndex - Index of the linkedParticipantGroup to link into
 * @returns {Promise<Object>} The updated group object
 */
export async function linkSingleParticipant(groupId, participant, linkedParticipantsIndex) {
    const data = await callApi(queries.linkSingleParticipant, {
        groupId,
        participant,
        linkedParticipantsIndex
    });

    if (!data || !data.linkSingleParticipant) throw new Error('Failed to link participant');

    const updatedGroup = data.linkSingleParticipant;
    saveGroupToStorage(updatedGroup);
    return updatedGroup;
}

/**
 * Unlinks a single participant from its linked participant group
 * @param {string} groupId - Group ID
 * @param {number} participant - Index of the participant to unlink
 * @returns {Promise<Object>} The updated group object
 */
export async function unlinkSingleParticipant(groupId, participant) {
    const data = await callApi(queries.unlinkSingleParticipant, { groupId, participant });
    if (!data || !data.unlinkSingleParticipant) throw new Error('Failed to unlink participant');

    const updatedGroup = data.unlinkSingleParticipant;
    saveGroupToStorage(updatedGroup);
    return updatedGroup;
}

// Rename a participant in a group
export async function renameParticipant(groupId, participantIndex, name) {
    const data = await callApi(queries.renameParticipant, {
        groupId,
        participant: participantIndex,
        name
    });

    if (!data || !data.renameParticipant) throw new Error('Failed to rename participant');

    const updatedGroup = data.renameParticipant;
    saveGroupToStorage(updatedGroup);
    return updatedGroup;
}


// Delete an expense from a group
export async function deleteExpense(groupId, expenseId) {
    const data = await callApi(queries.deleteExpense, {
        groupId,
        expenseId
    });

    if (!data || !data.deleteExpense) throw new Error('Failed to delete expense');

    const updatedGroup = data.deleteExpense;
    saveGroupToStorage(updatedGroup);
    return updatedGroup;
}

// Add a settled entry (mark as paid)
export async function addSettledEntry(groupId, input) {
    const data = await callApi(queries.addSettledEntry, {
        groupId,
        input
    });

    if (!data || !data.addSettledEntry) throw new Error('Failed to add settled entry');

    const updatedGroup = data.addSettledEntry;
    saveGroupToStorage(updatedGroup);
    return updatedGroup;
}

// Delete a settled entry (undo payment)
export async function deleteSettledEntry(groupId, entryId) {
    const data = await callApi(queries.deleteSettledEntry, {
        groupId,
        expenseId: entryId
    });

    if (!data || !data.deleteSettledEntry) throw new Error('Failed to delete settled entry');

    const updatedGroup = data.deleteSettledEntry;
    saveGroupToStorage(updatedGroup);
    return updatedGroup;
}

/**
 * Sends a magic link email to restore groups in the mobile app
 * @param {string} email - User's email address
 * @param {string[]} groupIds - Array of group IDs to restore
 * @returns {Promise<boolean>} True if successful
 */
export async function sendGroupMagicLink(email, groupIds) {
    try {
        const data = await callApi(queries.sendGroupMagicLink, {
            email,
            groupIds
        }, { silent: true });

        return data?.sendGroupMagicLink || null;
    } catch (e) {
        console.error('Failed to send magic link', e);
        return null;
    }
}

/**
 * Creates a new group/tab
 * @param {string} name - Group name
 * @param {string} currency - Currency code (e.g., 'USD', 'EUR')
 * @param {string[]} participants - Array of participant names
 * @returns {Promise<Object>} The created group object
 */
export async function createGroup(name, currency, participants) {
    const data = await callApi(queries.createGroup, {
        name,
        currency,
        participants
    });

    if (!data || !data.createGroup) throw new Error('Failed to create group');

    const newGroup = data.createGroup;
    saveGroupToStorage(newGroup);
    return newGroup;
}

/**
 * Creates a new group/tab directly from the web landing page (bypassing Lambda)
 * @param {string} name - Group name
 * @param {string} currency - Currency code (e.g., 'USD', 'EUR')
 * @returns {Promise<Object>} The created group object
 */
export async function createGroupFromWeb(name, currency) {
    const data = await callApi(queries.createGroupFromWeb, {
        name,
        currency
    });

    if (!data || !data.createGroupFromWeb) throw new Error('Failed to create group');

    const newGroup = data.createGroupFromWeb;
    saveGroupToStorage(newGroup);
    return newGroup;
}

/**
 * Updates group settings (e.g. simplify / minimize transactions)
 * @param {string} groupId - Group ID
 * @param {{ simplify?: boolean }} input - Settings to apply
 * @returns {Promise<Object>} The updated group object
 */
export async function setGroupSettings(groupId, input) {
    const data = await callApi(queries.setGroupSettings, { groupId, input });
    if (!data || !data.setGroupSettings) throw new Error('Failed to update group settings');

    const updatedGroup = data.setGroupSettings;
    saveGroupToStorage(updatedGroup);
    return updatedGroup;
}

/**
 * Removes a participant from a group
 * @param {string} groupId - Group ID
 * @param {number} participantIndex - Index of the participant to remove
 * @returns {Promise<Object>} The updated group object
 */
export async function removeParticipant(groupId, participantIndex) {
    const data = await callApi(queries.removeParticipant, {
        groupId,
        participant: participantIndex
    });

    if (!data || !data.removeParticipant) throw new Error('Failed to remove participant');

    const updatedGroup = data.removeParticipant;
    saveGroupToStorage(updatedGroup);
    return updatedGroup;
}

// Get the signed URL for receipt upload
export async function getReceiptUploadUrl() {
    const data = await callApi(queries.getReceiptUploadUrl, {}, {
        headers: { 'X-Web-Id': getWebId() }
    });

    if (!data || !data.getReceiptUploadUrl) throw new Error('Failed to get upload URL');
    return data.getReceiptUploadUrl;
}
// Create an expense from an uploaded receipt
export async function createExpenseFromReceipt(groupId, imageId, description, imageBase64) {
    const data = await callApi(queries.createExpenseFromReceipt, {
        groupId,
        imageId,
        description,
        imageBase64
    }, {
        headers: { 'X-Web-Id': getWebId() }
    });

    if (!data || !data.createExpenseFromReceipt) throw new Error('Failed to create expense from receipt');

    const updatedGroup = data.createExpenseFromReceipt;
    saveGroupToStorage(updatedGroup);
    return updatedGroup;
}

